<!-- ========================================== -->
<!-- NotificationBadge.vue (layouts/NotificationBadge.vue) -->
<!-- ========================================== -->

<template>
  <div v-if="shouldShowButton">
    <!-- Wrapper pour le bouton et le badge -->
    <div class="notification-trigger-wrapper">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            fab
            fixed
            bottom
            right
            color="orange darken-1"
            dark
            class="notification-trigger"
            style="z-index: 999; margin-bottom: 100px; margin-right: 15px;"
            @click="goToInvitations"
            v-on="on"
          >
            <i class="fas fa-user-plus"></i>
          </v-btn>
        </template>
        <span>
          <strong v-if="notificationCount > 0">
            {{ notificationCount }} Njangi invitation{{ notificationCount > 1 ? 's' : '' }} pending
          </strong>
          <span v-else>Njangi Invitations</span>
        </span>
      </v-tooltip>

      <!-- Badge personnalisé (même style que ChatbotComponent) -->
      <span v-if="notificationCount > 0" class="custom-badge">{{ notificationCount }}</span>
    </div>

    <!-- Dialog personnalisé avec bouton centré -->
    <v-dialog v-model="showDialog" max-width="550px" persistent>
      <v-card>
        <v-card-title class="headline primary darken-2 white--text" primary-title>
          ⚠️ Action Required - Pending Invitations
        </v-card-title>

        <v-card-text class="pt-4">
          <div style="text-align: left; line-height: 1.8;">
            <p style="font-size: 16px; font-weight: bold; color: #ff5722; margin-bottom: 15px;">
              🔔 You have {{ notificationCount }} pending Njangi invitation{{ notificationCount > 1 ? 's' : '' }}!
            </p>

            <p style="margin-bottom: 15px;">
              <strong>⚠️ IMPORTANT:</strong> The badge <span style="background: #f44336; color: white; padding: 2px 8px; border-radius: 10px; font-weight: bold;">🔴 {{ notificationCount }}</span> will NOT disappear until you process each invitation.
            </p>

            <div style="background: #fff3e0; padding: 15px; border-left: 4px solid #ff9800; margin: 15px 0;">
              <p style="margin: 0 0 10px 0; font-weight: bold; color: #e65100;">
                📋 To remove the badge, you MUST:
              </p>
              <ol style="margin: 5px 0 0 20px; padding: 0;">
                <li style="margin-bottom: 8px;">
                  ✅ Click <strong style="color: #4caf50;">"ACCEPT"</strong> to join the group
                </li>
                <li style="margin-bottom: 8px;">
                  ❌ OR click <strong style="color: #f44336;">"REFUSE"</strong> to decline the invitation
                </li>
              </ol>
            </div>

            <p style="margin-top: 15px; padding: 10px; background: #ffebee; border-radius: 5px; color: #c62828;">
              <strong>⛔ Warning:</strong> Simply viewing the invitations is NOT enough. You MUST accept or refuse each invitation for the badge to disappear.
            </p>

            <p style="margin-top: 15px; text-align: center; font-weight: bold; color: #1976d2;">
              👉 Check your invitations now and take action!
            </p>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="justify-center py-3">
          <v-btn color="teal" flat @click="closeDialog" class="px-5">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NotificationBadge',

  data() {
    return {
      notificationCount: 0,
      refreshInterval: null,
      showDialog: false
    };
  },

  computed: {
    shouldShowButton() {
      // Ne pas afficher sur la page des invitations Njangi
      const hideRoutes = ['NjangiInvitations'];
      const isNotOnInvitationsPage = !hideRoutes.includes(this.$route.name);

      // Afficher seulement s'il y a des notifications ET qu'on n'est pas sur la page invitations
      return isNotOnInvitationsPage && this.notificationCount > 0;
    }
  },

  mounted() {
    console.log('🎯 NotificationBadge mounted');
    this.loadNotifications();

    // Rafraîchir toutes les 10 secondes
    this.refreshInterval = setInterval(() => {
      this.loadNotifications();
    }, 10000);
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },

  methods: {
    async loadNotifications() {
      try {
        const response = await axios.get('/api/v1/njangi/notifications/');
        const newCount = response.data.pending_count || 0;

        // Forcer la réactivité
        this.$set(this, 'notificationCount', newCount);

        console.log('📬 Njangi Invitations:', this.notificationCount);
      } catch (error) {
        console.error('❌ Error loading Njangi notifications:', error);
      }
    },

    goToInvitations() {
      console.log('📧 Navigating to Njangi invitations');

      // Redirection vers la page des invitations
      this.$router.push({ name: 'NjangiInvitations' }).catch(err => {
        // Si déjà sur la page, juste afficher le message
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });

      // Afficher le dialog si des invitations en attente
      if (this.notificationCount > 0) {
        this.showDialog = true;
      }
    },

    closeDialog() {
      this.showDialog = false;
    }
  },

  watch: {
    notificationCount(newVal, oldVal) {
      console.log(`🔔 Njangi invitations: ${oldVal} → ${newVal}`);
    }
  }
};
</script>

<style scoped>
/* Wrapper pour le bouton et le badge */
.notification-trigger-wrapper {
  position: relative;
}

/* Badge personnalisé - EXACTEMENT comme ChatbotComponent */
.custom-badge {
  position: fixed;
  bottom: 135px; /* 100px (margin-bottom du bouton) + 35px */
  right: 45px;
  background: #f44336;
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.4);
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Bouton flottant - MÊMES proportions que ChatbotComponent */
.notification-trigger {
  width: 50px !important;
  height: 50px !important;
  min-width: 50px !important;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
  transition: all 0.3s ease;
}

.notification-trigger:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(255, 152, 0, 0.5) !important;
}

.notification-trigger i {
  font-size: 20px;
}

/* Centrer le bouton Close */
.justify-center {
  justify-content: center !important;
}

/* Responsive - Mobile */
@media (max-width: 480px) {
  .custom-badge {
    bottom: 155px; /* Ajusté pour mobile */
    right: 8px;
    width: 20px;
    height: 20px;
    font-size: 10px;
  }

  .notification-trigger {
    width: 45px !important;
    height: 45px !important;
    min-width: 45px !important;
    margin-bottom: 105px !important;
    margin-right: -15px !important;
  }

  .notification-trigger i {
    font-size: 18px;
  }
}
</style>